<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <div v-if="fields.backgroundImage" class="c-card-section__bg-media" aria-hidden="true">
      <AdaptiveImage
        v-if="fields.backgroundImage"
        class="bg-img"
        :field="fields.backgroundImage"
        :show="true"
        :enable-scroll-detection="false"
        :show-image-redius="false"
      />
    </div>

    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-card-section__inner">
        <header class="c-card-section__header">
          <span class="c-card-section__mark" aria-hidden="true"></span>
          <RichText
            v-if="fields.title?.value"
            class="c-card-section__title"
            :field="fields.title"
          />
        </header>

        <div v-if="cards.length" class="c-card-section__cards">
          <article v-for="card in cards" :key="card.id" class="c-card-section__card">
            <h3 v-if="card.fields.title?.value" class="c-card-section__card-title">
              <strong v-html="card.fields.title.value"></strong>
            </h3>
            <RichText
              v-if="card.fields.description?.value"
              class="c-card-section__card-description"
              :field="card.fields.description"
            />
          </article>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type { CardSectionCardFields, CardSectionProps } from '@/types/components/CardSection';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedCard extends ScItem<CardSectionCardFields> {
  fields: CardSectionCardFields;
}

const props = defineProps<CardSectionProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-card-section'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawCards = computed<ScItem<CardSectionCardFields>[]>(() => {
  const cardItems = utils.sc.getFieldObj(fields.value, 'cards');
  return Array.isArray(cardItems) ? cardItems : [];
});

const cards = computed<NormalizedCard[]>(() =>
  rawCards.value.filter((card): card is NormalizedCard => Boolean(card.fields))
);
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-card-section {
  position: relative;
  overflow: hidden;
  padding: 70px 0;
  color: $white;
  background:
    radial-gradient(circle at 9% 72%, rgba(142, 208, 160, 0.28) 0, transparent 34%),
    linear-gradient(145deg, #0c1219 0%, #111b23 52%, #25313b 100%);

  &__bg-media {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    pointer-events: none;

    .bg-img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__inner {
    position: relative;
    z-index: 3;
    max-width: 1440px;
    margin: 0 auto;
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__mark {
    display: block;
    width: 24px;
    height: 10px;
    margin-bottom: 28px;
    transform: skewX(-34deg);
    background: $color-growth-green;
    border-radius: 0 2px 0 2px;
  }

  &__title {
    margin: 0;
    color: $white;
    font-size: 40px;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 40px;
  }

  &__card {
    padding: 32px 24px;
    border: 1px solid rgba(213, 223, 229, 0.58);
    border-radius: 8px;
    background: rgba(6, 11, 16, 0.42);
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.06),
      0 18px 46px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(5px);
    text-align: center;
  }

  &__card-title {
    margin: 0;
    color: #e9f2e5;
    font-size: 28px;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__card-description {
    margin-top: 30px;
    color: rgba(255, 255, 255, 0.92) !important;
    font-size: 16px;
    line-height: 1.45;
    font-weight: 400;
  }

  @include tablet {
    &__bg-media {
      width: 100%;
      height: 100%;
      max-width: none;
    }

    &__cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 24px;
    }

    &__card-title {
      font-size: 24px;
    }

    &__card-description {
      margin-top: 26px;
      font-size: 16px;
      line-height: 1.42;
    }
  }
}
</style>
